// ** React
import { useEffect, useRef, useState, useContext } from "react"
import { useLocation } from "@reach/router"

// ** Gatsby
import { Link, navigate } from "gatsby"

// ** Custom Component
import NestedNavigationItem from "../NestedNavigationItem"
import NestedNavigationItemMobile from "../NestedNavigationItemMobile"

// ** 3rd Party Libraries
import { Transition } from "@headlessui/react"
import { classNames, parseLink } from "../../utils/helpers"
import { Logo, LogoFr, SingleKeyLogo } from "../Logo/Logo"
import { LogoHorizontal, LogoHorizontalFr } from "../Logo/Horizontal"
import i18n from "../../data/i18n_constants"
import PageContext from "../../contexts/PageContext"
import { useNavigationData } from "../../hooks/use-navigation-data"
import { StaticImage } from "gatsby-plugin-image"

const activeStyles = {
  color: "#DD944B",
}

interface NavigationProps {
  partnerLogo?: string
  pageContext?: any
}

function NavigationMenu(props: NavigationProps) {
  const { partnerLogo, pageContext } = props
  const [isMounted, setIsMounted] = useState<boolean>(false)
  // Determine if the link is active based on the location pathname

  const navRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const hamburgerRef = useRef<any>(null)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [offset, setOffset] = useState(0)
  const [isScroll, setIsScroll] = useState<boolean>(false)
  const [current, setCurrent] = useState<string>("")
  const page: any = useContext(PageContext)

  const {
    menu: { links },
  } = useNavigationData()
  useEffect(() => {
    setIsMounted(true)
  }, [])
  useEffect(() => {
    if (!isMounted) return
    const onScroll = () => setOffset(window.pageYOffset)
    // clean up code
    window.removeEventListener("scroll", onScroll)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [isMounted])

  useEffect(() => {
    if (!isMounted) return
    if (navRef && navRef.current) {
      if (offset > navRef.current.clientHeight) {
        setIsScroll(true)
      } else {
        setIsScroll(false)
      }
    }
  }, [isMounted, navRef, offset])

  useEffect(() => {
    const checkIfClickedOutside = (e: { target: any }) => {
      // If the menu is open and the clicked target is not within the menu,
      // then close the menu
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", checkIfClickedOutside)

    return () => {
      // Cleanup the event listener
      document.removeEventListener("mousedown", checkIfClickedOutside)
    }
  }, [isOpen])

  const location = useLocation()

  let i18nEnPath = "#",
    i18nFrPath = "#"

  const isEn = page.i18n_lang === "en_US"
  const isFr = page.i18n_lang === "fr_CA"

  if (isEn) {
    i18nEnPath = location.pathname

    const frPage = page.i18n_refs[0]
    if (frPage) {
      i18nFrPath = frPage.slug.current
    }
  } else if (isFr) {
    i18nFrPath = location.pathname

    const enPage = page.i18n_base
    if (enPage) {
      i18nEnPath = enPage.slug.current
    }
  }

  const HorizontalLogo = isFr
    ? LogoHorizontalFr
    : pageContext?.singleKey
    ? SingleKeyLogo
    : LogoHorizontal
  const RegularLogo = isFr
    ? LogoFr
    : pageContext?.singleKey
    ? SingleKeyLogo
    : Logo

  return (
    <header className="bg-white/95 backdrop-blur-sm fixed top-0 left-0 w-full z-50 shadow-md">
      <nav className="lg:max-w-5xl xl:max-w-7xl mx-auto">
        {/* Desktop Nav */}
        <div
          ref={navRef}
          className={classNames(
            isScroll ? "h-14" : "h-24",
            "w-full transition-all duration-200 items-center justify-between hidden lg:flex px-4"
          )}
        >
          <div>
            <Link
              to={
                isEn
                  ? pageContext?.singleKey
                    ? "/singlekey"
                    : pageContext?.exprior
                    ? "/experior-financial"
                    : "/"
                  : "/francais"
              }
              aria-label="Back to 8Twleve Home"
              tabIndex={0}
            >
              {isScroll ? (
                partnerLogo ? (
                  <img
                    className="w-[500px] cursor-pointer"
                    src={partnerLogo}
                    alt="8Twelve Mortgage"
                    aria-label="8Twelve Mortgage"
                    onClick={() => navigate("/")}
                  />
                ) : pageContext?.singleKey ? (
                  <StaticImage
                    objectFit="contain"
                    className="h-[40px] w-[170px] md:h-[25px] md:w-[170px] cursor-pointer"
                    placeholder="blurred"
                    src="../../images/singleKey/logo.png"
                    alt="8Twelve Mortgage"
                    quality={100}
                  />
                ) : pageContext?.exprior ? (
                  <StaticImage
                    objectFit="contain"
                    className="h-[40px] w-[170px] md:h-[25px] md:w-[170px] cursor-pointer"
                    placeholder="blurred"
                    src="../../images/exprior/logo.png"
                    alt="8Twelve Mortgage"
                    quality={100}
                  />
                ) : (
                  <HorizontalLogo
                    className={`${
                      pageContext?.singleKey ? "" : "h-14 w-40 ml-2"
                    }`}
                    aria-label="8Twelve Mortgage"
                  />
                )
              ) : partnerLogo ? (
                <img
                  className={"w-[500px] cursor-pointer"}
                  src={partnerLogo}
                  alt="8Twelve Mortgage"
                  aria-label="8Twelve Mortgage"
                  onClick={() => navigate("/")}
                />
              ) : pageContext?.singleKey ? (
                <StaticImage
                  objectFit="contain"
                  className="cursor-pointer"
                  placeholder="blurred"
                  src="../../images/singleKey/logo.png"
                  alt="8Twelve Mortgage"
                  quality={100}
                />
              ) : pageContext?.exprior ? (
                <StaticImage
                  objectFit="contain"
                  className="h-[40px] w-[320px] md:h-[70px] md:w-[320px] cursor-pointer"
                  placeholder="blurred"
                  src="../../images/exprior/logo.png"
                  alt="8Twelve Mortgage"
                  quality={100}
                />
              ) : (
                <RegularLogo
                  className={`${pageContext?.singleKey ? "" : "h-16 w-44"}`}
                  aria-label="8Twelve Mortgage"
                />
              )}
            </Link>
          </div>
          {!partnerLogo && (
            <div className="flex items-center gap-x-4">
              {!pageContext?.singleKey &&
                !pageContext?.exprior &&
                links.length > 0 &&
                links.map((link: any, idx: any) => {
                  if (link.mobile_only) return false

                  if (link.links && link.links.length > 1) {
                    return (
                      <NestedNavigationItem
                        setCurrent={setCurrent}
                        current={current}
                        key={idx}
                        title={isEn ? link.title : link.i18n_refs[0]?.title}
                        links={isEn ? link.links : link.i18n_refs[0]?.links}
                      />
                    )
                  } else {
                    return (
                      <Link
                        onKeyUp={() => setCurrent(link.title)}
                        tabIndex={0}
                        key={idx}
                        to={
                          isEn ? parseLink(link) : parseLink(link.i18n_refs[0])
                        }
                        activeStyle={activeStyles}
                        className="text-gray-700 active:text-blue-500 hover:text-blue-500 px-3 py-2 rounded-md text-base font-medium"
                      >
                        {isEn ? link.title : link.i18n_refs[0]?.title}
                      </Link>
                    )
                  }
                })}
              {(pageContext?.singleKey || pageContext?.exprior) && (
                <>
                  <Link
                    tabIndex={0}
                    to={
                      pageContext?.exprior
                        ? "/experior-financial"
                        : "/singlekey"
                    }
                    activeStyle={activeStyles}
                    className="text-gray-700 active:text-blue-500 hover:text-blue-500 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Home
                  </Link>
                  <Link
                    tabIndex={0}
                    to={
                      pageContext?.exprior
                        ? "/experior-financial/about-8twelve"
                        : "/singlekey/about-8twelve"
                    }
                    activeStyle={activeStyles}
                    className="text-gray-700 active:text-blue-500 hover:text-blue-500 px-3 py-2 rounded-md text-base font-medium"
                  >
                    About 8Twelve
                  </Link>
                  <Link
                    tabIndex={0}
                    to={
                      pageContext?.exprior
                        ? "/experior-financial/mortgage-solution"
                        : "/singlekey/mortgage-solution"
                    }
                    activeStyle={activeStyles}
                    className="text-gray-700 active:text-blue-500 hover:text-blue-500 px-3 py-2 rounded-md text-base font-medium"
                  >
                    Mortgage Solutions
                  </Link>
                </>
              )}

              <Link
                onKeyUp={() => setCurrent(`Let's Talk`)}
                to={
                  page._id === "a2d65ebd-f642-4ecd-84f8-5ef2fb80bd8f"
                    ? "/join-us"
                    : pageContext?.singleKey
                    ? "/singlekey/application"
                    : pageContext?.exprior
                    ? "https://partner.8twelve.co/experior-financial/"
                    : isEn
                    ? "/application"
                    : "/application-francais"
                }
                className={classNames(
                  isScroll ? "py-2" : "py-3",
                  "text-white bg-orange-500 rounded-full px-4 lg:px-14 text-base font-medium"
                )}
              >
                {page._id === "a2d65ebd-f642-4ecd-84f8-5ef2fb80bd8f"
                  ? "Join Us"
                  : pageContext?.singleKey
                  ? "Apply Now"
                  : pageContext?.exprior
                  ? "Register Now"
                  : i18n.lets_talk[page.i18n_lang]}
              </Link>
              {!pageContext?.singleKey && (
                <div>
                  <Link
                    to={i18nEnPath}
                    className={`text-gray-700 hover:text-blue-500 py-2 px-1 rounded-md text-base ${
                      isEn ? "font-bold" : ""
                    }`}
                  >
                    en
                  </Link>
                  {"|"}

                  <Link
                    to={i18nFrPath}
                    className={`text-gray-700 hover:text-blue-500 py-2 px-1 rounded-md text-base ${
                      isFr ? "font-bold" : ""
                    }`}
                  >
                    fr
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Nav */}
        <div className="lg:hidden">
          <div className="shadow-md px-6 bg-white fixed w-full z-20">
            <div className="flex items-center justify-between">
              <Link
                to={
                  isEn
                    ? pageContext?.singleKey
                      ? "/singlekey"
                      : "/"
                    : "/francais"
                }
                aria-label="Go back to Twelve Mortgage"
                tabIndex={0}
              >
                {partnerLogo ? (
                  <img
                    className="w-[400px] cursor-pointer"
                    src={partnerLogo}
                    alt="8Twelve Mortgage"
                    aria-label="8Twelve Mortgage"
                    onClick={() => navigate("/")}
                  />
                ) : pageContext?.singleKey ? (
                  <StaticImage
                    objectFit="contain"
                    className="h-[40px] w-[170px] md:h-[25px] md:w-[170px] cursor-pointer"
                    placeholder="blurred"
                    src="../../images/singleKey/logo.png"
                    alt="8Twelve Mortgage"
                    quality={100}
                  />
                ) : (
                  <HorizontalLogo
                    className="h-14 w-40 ml-2"
                    aria-label="8Twelve Mortgage"
                  />
                )}
              </Link>

              <div className="pb-2 flex justify-center items-center">
                {page._id === "a2d65ebd-f642-4ecd-84f8-5ef2fb80bd8f" && (
                  <div className="flex justify-center mt-1 mr-2">
                    <Link
                      to={"/join-us"}
                      className="text-white bg-orange-500 rounded-full py-2 px-6 text-l font-extrabold"
                    >
                      Join Us
                    </Link>
                  </div>
                )}
                {/* Hamburger Icon */}
                <button
                  ref={hamburgerRef}
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-6 h-6 relative focus:outline-none"
                >
                  <span className="sr-only">Open main menu</span>
                  <div className="block w-6 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 ">
                    <span
                      aria-hidden="true"
                      className={`block absolute h-0.5 w-6 bg-[#32302f] transform duration-[350ms] rounded-[1px] ease-in-out ${
                        isOpen ? "rotate-45" : "translate-y-[0.2rem]"
                      }`}
                    ></span>
                    <span
                      aria-hidden="true"
                      className={`block absolute h-0.5 w-6 bg-[#32302f] transform duration-[350ms] rounded-[1px] ease-in-out ${
                        isOpen ? "-rotate-45" : "-translate-y-[0.2rem]"
                      }`}
                    ></span>
                  </div>
                </button>
              </div>
            </div>
          </div>
          <Transition
            show={isOpen}
            enter="transition ease-out duration-100 transform"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75 transform"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            className="h-full scrollbar-hide"
          >
            <div
              className="pt-12 lg:hidden shadow-md w-full min-h-full h-max pb-10 bg-white max-h-screen overflow-auto"
              id="mobile-menu"
              ref={menuRef}
            >
              {/* Navigation List */}
              <div className="px-6 pt-2 pb-3 space-y-1 sm:px-3">
                {links?.length > 0 &&
                  links.map((link: any, idx: any) => {
                    if (link.links && link.links.length > 1) {
                      return (
                        <NestedNavigationItemMobile
                          key={idx}
                          title={isEn ? link.title : link.i18n_refs[0]?.title}
                          links={isEn ? link.links : link.i18n_refs[0]?.links}
                        />
                      )
                    } else {
                      return (
                        <div
                          key={idx}
                          className="flex w-full border-b border-b-gray-300"
                        >
                          <Link
                            to={
                              isEn
                                ? parseLink(link)
                                : parseLink(link.i18n_refs[0])
                            }
                            activeStyle={activeStyles}
                            className="text-gray-700 text-left w-full cursor-pointer hover:text-blue-500 block py-4 rounded-md text-2xl font-extrabold"
                          >
                            {isEn ? link.title : link.i18n_refs[0]?.title}
                          </Link>
                        </div>
                      )
                    }
                  })}
              </div>

              <div className="flex justify-center mt-6">
                <Link
                  to={isEn ? "/application" : "/application-francais"}
                  className="text-white bg-orange-500 rounded-full py-2 px-12 text-2xl font-extrabold"
                >
                  {i18n.lets_talk[page.i18n_lang]}
                </Link>
              </div>

              <div className="flex justify-center mt-6 text-2xl">
                <Link
                  to={i18nEnPath}
                  className={`text-gray-700 hover:text-blue-500 px-2 rounded-md ${
                    isEn ? "font-bold" : ""
                  }`}
                >
                  English
                </Link>
                {"|"}

                <Link
                  to={i18nFrPath}
                  className={`text-gray-700 hover:text-blue-500 px-2 rounded-md ${
                    isFr ? "font-bold" : ""
                  }`}
                >
                  Français
                </Link>
              </div>
            </div>
          </Transition>
        </div>
      </nav>
    </header>
  )
}

export default NavigationMenu
